import { useModalContentToggler } from '@components'

import { Button, ButtonsGroup, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useSegment } from '../../../../hooks'
import { formatDate } from '../../../../utils'
import {
  useManualPayment,
  useMultiplePayableSubjects,
  usePaymentModal,
} from '../../../paymentReconciliationModal/contexts'
import { PayableSubject } from '../../../paymentReconciliationModal/types'
import { getTypeFromPayableSubject } from '../../../paymentReconciliationModal/utils'
import { getCashSide } from '../../../paymentReconciliationModal/utils/getCashSide'
import { SubjectReconciliationModalStateId } from '../../enums/SubjectReconciliationModalStateId'
import { useRegisterManualPayment } from '../../hooks/useRegisterManualPayment'
import { MultipleSubjectsButton } from '../MultipleSubjectsButton'

export const ManualPaymentModalFooter = <T extends PayableSubject>(): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { modalId, selectedDifferenceType, selectedAccountId, paymentDate, isCrossCurrency } = usePaymentModal()
  const { initialSubject, isCashBasis } = useMultiplePayableSubjects()
  const { hasManualPaymentDifference, manualPaymentAmount, manualPaymentDifference, crossCurrencyPaymentAmount } =
    useManualPayment()

  const { close: closeManualPaymentModal } = useModal(modalId)

  const payableSubjectType = getTypeFromPayableSubject(initialSubject)

  const { registerManualPayment, isProcessing } = useRegisterManualPayment<T>({
    cashSide: getCashSide(payableSubjectType, initialSubject?.type),
    crossCurrencyPaymentAmount,
    isCrossCurrency,
    selectedDifferenceType,
    selectedAccountId,
    manualPaymentAmount,
    manualPaymentDifference,
    payableSubjectType,
    paymentDate: formatDate(paymentDate),
    onSuccess: () => closeManualPaymentModal(),
  })

  const { setActiveState } = useModalContentToggler()

  // Difference type does not need to be selected if the payment is cross currency
  const isMissingDifferenceType = hasManualPaymentDifference && !selectedDifferenceType && !isCrossCurrency
  const isMissingRequiredFields = useMemo(
    () => !paymentDate || !manualPaymentAmount,
    [paymentDate, manualPaymentAmount],
  )

  const isEnterPaymentButtonDisabled = useMemo(
    () => isMissingDifferenceType || isMissingRequiredFields || isProcessing,
    [isMissingDifferenceType, isMissingRequiredFields, isProcessing],
  )

  const handleEnterPaymentButton = useCallback(() => {
    registerManualPayment()
  }, [registerManualPayment])

  const handleRegisterMultipleBillsButton = useCallback(() => {
    track('xxx - expenses - Manual payment modal - register mutliple bills clicked')

    setActiveState(SubjectReconciliationModalStateId.MultipleSubjects)
  }, [setActiveState, track])

  return (
    <ButtonsGroup>
      {!isCashBasis && (
        <MultipleSubjectsButton onClick={handleRegisterMultipleBillsButton} payableSubjectType={payableSubjectType} />
      )}
      <Button onClick={handleEnterPaymentButton} disabled={isEnterPaymentButtonDisabled}>
        {t('bill.payment_reconcile.button.enter_payment')}
      </Button>
    </ButtonsGroup>
  )
}
